import { useEffect, useState } from "react";

import { api, clinicHeader, readError } from "../api/client";
import { ErrorNote } from "./ErrorNote";
import { SlotPicker, type Slot } from "./SlotPicker";

type Match = {
  id: string;
  full_name: string;
  mrn: string;
  // Masked, because the desk only needs to recognise the right record and the
  // whole number belongs on the chart (IDN-F-007).
  identifier_masked: string;
};

type Props = {
  onClose: () => void;
  onBooked: () => void;
};

/**
 * Book a patient in over the telephone (SCH-F-024).
 *
 * Most of a Malaysian clinic's morning arrives this way, so this is the ordinary
 * path and not a fallback. It goes through the same two-phase claim the patient
 * app uses, which is what stops a telephone call and a tap in the app taking the
 * same slot.
 *
 * The patient comes first, because the desk has somebody on the line and needs
 * to know they have the right record before offering a time.
 */
export function BookForPatient({ onClose, onBooked }: Props) {
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState<Match[]>([]);
  const [chosen, setChosen] = useState<Match | null>(null);
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (chosen || query.trim().length < 2) {
      setMatches([]);
      return;
    }

    let cancelled = false;
    // A search on every keystroke would send one request per letter. A quarter
    // of a second is long enough to stop that and short enough that the desk
    // does not notice it.
    const timer = setTimeout(async () => {
      const { data, error: failed } = await api.GET("/portal/patients", {
        params: { header: clinicHeader(), query: { q: query.trim() } },
      });
      if (cancelled) return;
      if (failed) {
        setError(readError(failed).message);
        return;
      }
      setError(null);
      // The search answers with a bare array, which the generated type says
      // and the compiler enforces.
      setMatches((data ?? []) as Match[]);
    }, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, chosen]);

  async function book(slot: Slot) {
    if (!chosen) return;

    setBusy(true);
    const { error: failed } = await api.POST("/portal/appointments", {
      params: { header: clinicHeader() },
      body: { patient_id: chosen.id, slot_id: slot.id, reason },
    });
    setBusy(false);
    if (failed) {
      setError(readError(failed).message);
      return;
    }
    onBooked();
  }

  return (
    <div className="panel">
      <div className="row-between">
        <h2>Book someone in</h2>
        <button type="button" className="link" onClick={onClose}>
          Close
        </button>
      </div>

      <ErrorNote message={error} />

      {!chosen && (
        <>
          <label className="field">
            Who is on the telephone
            <input
              type="search"
              value={query}
              autoFocus
              placeholder="IC number, name, MRN, or email"
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>

          {query.trim().length >= 2 && matches.length === 0 && (
            <p className="empty">
              Nobody here by that. A new patient registers in the app before the
              clinic can book them.
            </p>
          )}

          {matches.length > 0 && (
            <ul className="matches">
              {matches.map((row) => (
                <li key={row.id}>
                  <button type="button" className="link" onClick={() => setChosen(row)}>
                    {row.full_name}
                  </button>
                  <span className="quiet">
                    {row.mrn || row.identifier_masked || "no number on file"}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </>
      )}

      {chosen && (
        <>
          <p>
            <strong>{chosen.full_name}</strong>{" "}
            <button type="button" className="link" onClick={() => setChosen(null)}>
              change
            </button>
          </p>

          <label className="field">
            What they said it is about
            <input
              type="text"
              value={reason}
              maxLength={300}
              placeholder="Cough for a week"
              onChange={(event) => setReason(event.target.value)}
            />
          </label>

          {/* Their own words, and not a diagnosis. It reaches the doctor's day
              list so the room is ready, and it is not a clinical record. */}
          <p className="quiet">
            Their words, not a diagnosis. It shows on the day's list.
          </p>

          <SlotPicker onPick={(slot) => void book(slot)} busy={busy} />
        </>
      )}
    </div>
  );
}
